import { DestroyRef, inject, Injectable } from '@angular/core';
import { BackendService } from './backend.service';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompanySummary } from 'src/app/models/generic-models/views/CompanySummary';
import { FrontendText } from 'src/app/models/generic-models/views/FrontendText';

// --- New: support both payload shapes ---
type FlatOverrides = Record<string, string>;
type LegacyOverrides = FrontendText[];
type OverridesApiResponse = FlatOverrides | LegacyOverrides;

type OverridesPayload = {
  company: CompanySummary;
  lang: string;
  overrides: FlatOverrides; // normalized
};

function isFlatOverrides(x: unknown): x is FlatOverrides {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}
function isLegacyOverrides(x: unknown): x is LegacyOverrides {
  return Array.isArray(x);
}

@Injectable({ providedIn: 'root' })
export class TranslationsOverrideService {
  private _companyChange$ = new BehaviorSubject<CompanySummary | null>(null);

  private _lang$: Observable<string> = this.translate.onLangChange.pipe(
    map((e) => e.lang),
    startWith(this.translate.currentLang),
    distinctUntilChanged(),
  );

  /** Emits normalized { company, lang, overrides } whenever either changes */
  readonly overrides$: Observable<OverridesPayload> = combineLatest([
    this._companyChange$.pipe(
      filter((c): c is CompanySummary => !!c),
      distinctUntilChanged((a, b) => a.id === b.id),
    ),
    this._lang$,
  ]).pipe(
    switchMap(([company, lang]) =>
      this.backendService.getCompaniesTextOverrides$(company.id, lang).pipe(
        map((raw: any) => this.normalizeOverrides(raw, lang)),
        map((overrides) => ({ company, lang, overrides })),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private _destroyRef = inject(DestroyRef);

  constructor(
    private backendService: BackendService,
    private translate: TranslateService,
  ) {
    this.overrides$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(({ lang, overrides }) => {
      this.applyOverridesToTranslate(overrides, lang);
    });
  }

  /** Call this when the selected company changes */
  public companyChanged(company: CompanySummary) {
    this._companyChange$.next(company);
  }

  /** Convert either API shape to a flat key→text map */
  private normalizeOverrides(input: OverridesApiResponse, lang: string): FlatOverrides {
    if (isFlatOverrides(input)) {
      // New backend: already flat
      return input;
    }
    if (isLegacyOverrides(input)) {
      // Old backend: FrontendText[]
      const flat: FlatOverrides = {};
      for (const ft of input) {
        const loc = ft.value?.find((v) => v.language === lang);
        if (loc?.text != null) flat[ft.key] = String(loc.text);
      }
      return flat;
    }
    // Fallback: nothing usable
    return {};
  }

  /** Apply to ngx-translate (keeps your "translations" namespace convention) */
  private applyOverridesToTranslate(flat: FlatOverrides, lang: string): void {
    if (!flat || Object.keys(flat).length === 0) return;

    // If your templates use keys like "translations.thank-you-2",
    // keep the namespace. If templates use plain keys, drop the wrapper.
    this.translate.setTranslation(lang, { translations: flat }, true);

    // Optional nudge for change detection:
    // this.translate.use(lang);
  }
}
